import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, Plus, Minus, Scale, X } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import LotteryCodeInput from "@/components/LotteryCodeInput";
import InlinePOSKeypad from "@/components/pos/InlinePOSKeypad";
import GestionaleLongPress from "@/components/pos/GestionaleLongPress";
import PrecontoButton from "@/components/pos/PrecontoButton";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

// Configurazione per modalità a peso
const MODE_CONFIG = {
  alimentari: { label: "Alimentari", color: "#14B8A6" },
  macelleria: { label: "Macelleria", color: "#BE123C" },
  panetteria: { label: "Panetteria", color: "#F59E0B" },
};

export default function POSPesati() {
  const loc = useLocation();
  // determina modalità da URL: /cassa/alimentari, /cassa/macelleria, /cassa/panetteria
  const mode = (loc.pathname.split("/").pop() || "alimentari");
  const conf = MODE_CONFIG[mode] || MODE_CONFIG.alimentari;

  const [tenant, setTenant] = useState(null);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [lines, setLines] = useState([]);
  const [showPay, setShowPay] = useState(false);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [weighProd, setWeighProd] = useState(null);
  const [weightInput, setWeightInput] = useState("");
  const [lotteryCode, setLotteryCode] = useState("");
  const [discountPct, setDiscountPct] = useState(0);

  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data));
    api.get(`/categories?mode=${mode}`).then((r) => setCats(Array.isArray(r.data) ? r.data : []));
    api.get(`/products?mode=${mode}`).then((r) => setProds(Array.isArray(r.data) ? r.data : []));
  }, []);

  useEffect(() => {
    if (activeCat == null && cats.length && prods.length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const filtered = useMemo(() => (Array.isArray(prods) ? prods : []).filter(p => p.category_id === activeCat && p.visible !== false), [prods, activeCat]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty, 0);
  const total = discountPct > 0 ? subtotal * (1 - discountPct / 100) : subtotal;

  const addFreeLine = (amount) => {
    setLines((prev) => [...prev, { product_id: null, name: "Vendita libera", qty: 1, price: amount, vat: 22 }]);
    toast.success(`+${formatEUR(amount)}`);
  };
  const applyDiscount = ({ mode: m, value }) => {
    const pct = m === "pct" ? Math.min(100, Math.max(0, value)) : (subtotal > 0 ? Math.min(100, (value / subtotal) * 100) : 0);
    setDiscountPct(pct);
    toast.success(pct > 0 ? `Sconto ${pct.toFixed(1)}% applicato` : "Sconto rimosso");
  };
  const [precontoOrderId, setPrecontoOrderId] = useState(null);
  const saveOrderForGestionale = async () => {
    if (!lines.length) { toast.error("Nessun articolo"); return null; }
    try {
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines, discount_pct: discountPct || 0 });
        return r.data;
      }
      const r = await api.post("/orders", { mode, lines, discount_pct: discountPct || 0 });
      return r.data;
    } catch { toast.error("Errore"); return null; }
  };
  const onGestionaleClosed = () => { setLines([]); setDiscountPct(0); setLotteryCode(""); setPrecontoOrderId(null); };

  const onProductClick = (p) => {
    if (p.weighable) {
      setWeighProd(p);
      setWeightInput("");
    } else {
      addProd(p, 1);
    }
  };

  const confirmWeigh = () => {
    if (!weighProd) return;
    const grams = parseFloat(weightInput);
    if (isNaN(grams) || grams <= 0) return toast.error("Peso non valido");
    const kg = grams / 1000;
    const lineTotal = kg * weighProd.price;
    setLines([...lines, {
      product_id: weighProd.id,
      name: `${weighProd.name} (${grams}g)`,
      qty: 1,
      price: parseFloat(lineTotal.toFixed(2)),
      vat: weighProd.vat,
      weight_kg: kg,
      unit: "kg",
    }]);
    toast.success(`${grams}g · ${formatEUR(lineTotal)}`);
    setWeighProd(null);
    setWeightInput("");
  };

  const addProd = (p, qty = 1) => {
    const i = lines.findIndex(l => l.product_id === p.id && !l.weight_kg);
    if (i >= 0) {
      const u = [...lines]; u[i] = { ...u[i], qty: u[i].qty + qty }; setLines(u);
    } else {
      setLines([...lines, { product_id: p.id, name: p.name, qty, price: p.price, vat: p.vat, unit: "pz" }]);
    }
  };

  const incLine = (i, d) => {
    const u = [...lines];
    // per i pesati non incrementare in qty: solo eliminazione
    if (u[i].weight_kg) {
      if (d < 0) u.splice(i, 1);
      setLines(u);
      return;
    }
    u[i] = { ...u[i], qty: Math.max(0, u[i].qty + d) };
    if (u[i].qty === 0) u.splice(i, 1);
    setLines(u);
  };

  const close = async (method) => {
    const pos = await authorizeCardIfNeeded({ method, amount: total });
    if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
    if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    const offlineId = newOfflineId();
    let oid = precontoOrderId || null;
    try {
      const payload = { mode, lines, payment_method: method, discount_pct: discountPct || 0, offline_id: offlineId };
      if (lotteryCode) payload.lottery_code = lotteryCode;
      if (precontoOrderId) {
        const upd = { lines, discount_pct: discountPct || 0 };
        if (lotteryCode) upd.lottery_code = lotteryCode;
        await api.patch(`/orders/${precontoOrderId}`, upd, SERVER_ATTEMPT);
        oid = precontoOrderId;
      } else {
        const r = await api.post("/orders", payload, SERVER_ATTEMPT);
        oid = r.data.id;
      }
      const c = await api.post(`/orders/${oid}/close-fiscal`, { payment_method: method, discount_pct: discountPct || 0 }, SERVER_ATTEMPT);
      setLast(c.data); setShowPay(false); setShowScontrino(true); setLines([]); setLotteryCode(""); setDiscountPct(0); setPrecontoOrderId(null);
      toast.success(`Scontrino #${c.data.receipt_number}`);
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid, mode, lines,
        payment_method: method, discount_pct: discountPct || 0, lottery_code: lotteryCode || null,
      });
      if (off.printed) {
        setLast(off.order); setShowPay(false); setShowScontrino(true); setLines([]); setLotteryCode(""); setDiscountPct(0); setPrecontoOrderId(null);
        return;
      }
      if (!off.handled) toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  // Numeric keypad helper
  const press = (v) => {
    if (v === "C") setWeightInput("");
    else if (v === "←") setWeightInput(weightInput.slice(0, -1));
    else setWeightInput(weightInput + v);
  };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" data-testid="pesati-back" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">Velta One · {conf.label}</div>
      </header>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <section className="col-span-8 flex flex-col overflow-hidden">
          <div className="h-20 border-b border-velta-border bg-white flex gap-2 overflow-x-auto pos-scroll p-3">
            {(Array.isArray(cats) ? cats : []).map(c => (
              <button key={c.id} data-testid={`pesati-cat-${c.id}`} onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-14 px-5 rounded-md font-medium btn-tactile ${activeCat === c.id ? "text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                style={activeCat === c.id ? { background: c.color } : {}}>{c.name}</button>
            ))}
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3">
            <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
              {(Array.isArray(filtered) ? filtered : []).map(p => {
                const cat = cats.find(c => c.id === p.category_id);
                const color = p.color || cat?.color || "#0A0A0A";
                return (
                  <button
                    key={p.id}
                    data-testid={`pesati-prod-${p.id}`}
                    onClick={() => onProductClick(p)}
                    className="relative bg-white border border-velta-border rounded-md p-4 h-28 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile"
                  >
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: color }} />
                    {p.weighable && (
                      <div className="absolute top-2 right-2 bg-[#2F9E77] text-white text-[10px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                        <Scale className="w-3 h-3" /> /KG
                      </div>
                    )}
                    <div className="font-medium text-sm mt-2 pr-10">{p.name}</div>
                    {p.show_price && (
                      <div className="font-mono font-bold text-lg">
                        {formatEUR(p.price)}{p.weighable && <span className="text-xs font-normal text-velta-mute">/kg</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="col-span-4 border-l border-velta-border bg-white flex flex-col overflow-hidden">
          <div className="p-4 border-b border-velta-border">
            <h2 className="font-heading font-semibold text-lg">Scontrino</h2>
          </div>
          <div className="flex-1 overflow-auto pos-scroll p-3">
            {lines.length === 0 ? <div className="text-center text-sm text-velta-mute p-8">Tocca un articolo. Quelli con etichetta <span className="font-bold text-[#2F9E77]">/KG</span> chiedono il peso.</div> : (
              <div className="space-y-2">
                {(Array.isArray(lines) ? lines : []).map((l, i) => (
                  <div key={i} className="border border-velta-border rounded-md p-3">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-sm">
                        {l.name}
                        {l.weight_kg && <Scale className="w-3 h-3 inline ml-1 text-[#2F9E77]" />}
                      </div>
                      <button onClick={() => incLine(i, -l.qty)} className="text-velta-error"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex items-center justify-between">
                      {l.weight_kg ? (
                        <span className="text-xs text-velta-mute font-mono">{(l.weight_kg * 1000).toFixed(0)}g</span>
                      ) : (
                        <div className="inline-flex items-center border border-velta-border rounded-md">
                          <button onClick={() => incLine(i, -1)} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="w-8 text-center font-mono font-medium">{l.qty}</span>
                          <button onClick={() => incLine(i, 1)} className="w-8 h-8 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                      <div className="font-mono font-bold">{formatEUR(l.price * l.qty)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border-t border-velta-border p-3 bg-velta-bg">
            <GestionaleLongPress orderId={null} total={total} saveOrder={saveOrderForGestionale} onClosed={onGestionaleClosed}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
                <span data-testid="pesati-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(total)}</span>
              </div>
              {discountPct > 0 && (
                <div className="text-[10px] font-mono text-velta-success text-right mb-1">
                  Sconto -{discountPct.toFixed(1)}% (subtot. {formatEUR(subtotal)})
                </div>
              )}
            </GestionaleLongPress>
            <div className="mb-2">
              <InlinePOSKeypad onAddFreePrice={addFreeLine} onApplyDiscount={applyDiscount} currentTotal={total} compact />
            </div>
            <PrecontoButton mode={mode} lines={lines} discountPct={discountPct} tenant={tenant} orderId={precontoOrderId} onOrderSaved={setPrecontoOrderId} testid="pesati-preconto-btn" />
            <button data-testid="pesati-pay-btn" disabled={!lines.length} onClick={() => setShowPay(true)} className="w-full h-14 text-white rounded-md font-medium text-lg flex items-center justify-center gap-2 btn-tactile disabled:opacity-40" style={{ background: conf.color }}>
              <Receipt className="w-5 h-5" /> Scontrino
            </button>
          </div>
        </aside>
      </div>

      {/* Weight modal */}
      {weighProd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setWeighProd(null)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold">{weighProd.name}</h3>
              <button onClick={() => setWeighProd(null)} data-testid="pesati-weigh-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-center text-velta-mute text-sm mb-2">Prezzo: <span className="font-mono font-bold text-velta-ink">{formatEUR(weighProd.price)}/kg</span></div>
            <div className="bg-velta-bg rounded-md p-6 mb-4 text-center">
              <div className="text-xs text-velta-mute mb-1">Peso (grammi)</div>
              <div data-testid="pesati-weight-display" className="font-mono font-bold text-5xl tracking-tight">{weightInput || "0"} <span className="text-xl text-velta-mute">g</span></div>
              {weightInput && (
                <div className="text-xs text-velta-mute mt-2">
                  = <span className="font-mono font-bold text-[#2F9E77]">{formatEUR((parseFloat(weightInput) / 1000) * weighProd.price)}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {["1","2","3","4","5","6","7","8","9","C","0","←"].map(k => (
                <button
                  key={k}
                  data-testid={`pesati-key-${k}`}
                  onClick={() => press(k)}
                  className={`h-14 rounded-md font-mono font-bold text-xl btn-tactile ${k === "C" ? "bg-red-50 text-red-700 border border-red-200" : k === "←" ? "bg-velta-bg border border-velta-border" : "bg-white border border-velta-border hover:border-velta-ink"}`}
                >
                  {k}
                </button>
              ))}
            </div>
            <button
              data-testid="pesati-weigh-confirm"
              disabled={!weightInput || parseFloat(weightInput) <= 0}
              onClick={confirmWeigh}
              className="w-full h-12 text-white rounded-md font-medium btn-tactile disabled:opacity-40"
              style={{ background: conf.color }}
            >
              Aggiungi al conto
            </button>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {showPay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowPay(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold mb-4">Pagamento</h3>
            <div className="bg-velta-bg rounded-md p-4 mb-4 text-center"><div className="font-mono font-bold text-3xl">{formatEUR(total)}</div></div>
            <div className="mb-4"><LotteryCodeInput value={lotteryCode} onChange={setLotteryCode} testid="pesati-lottery" /></div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="pesati-pay-cash" onClick={() => close("cash")} className="h-14 bg-velta-ink text-white rounded-md font-medium btn-tactile">Contanti</button>
              <button data-testid="pesati-pay-card" onClick={() => close("card")} className="h-14 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Carta</button>
              <button data-testid="pesati-pay-ticket" onClick={() => close("ticket")} className="h-14 bg-velta-bg border border-velta-border text-velta-ink rounded-md font-medium btn-tactile">Ticket</button>
            </div>
          </div>
        </div>
      )}
      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}
